/**
 * OppgaveListe - Vis og håndter gjøremål/oppgaver per sak
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './OppgaveListe.css';

export default function OppgaveListe({ sakId }) {
  const { api } = useAuth();
  const [oppgaver, setOppgaver] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNyOppgave, setShowNyOppgave] = useState(false);
  const [redigerOppgave, setRedigerOppgave] = useState(null);
  const [nyOppgave, setNyOppgave] = useState({
    tittel: '',
    beskrivelse: '',
    type: 'dokument',
    prioritet: 'middels',
    estimert_timer: 1,
    forfallsdato: ''
  });
  const [aiEstimat, setAiEstimat] = useState(null);
  const [lasterEstimat, setLasterEstimat] = useState(false);

  useEffect(() => {
    if (sakId) {
      lastOppgaver();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sakId]);

  const lastOppgaver = async () => {
    try {
      setLoading(true);
      const response = await api(`/oppgaver?sak_id=${sakId}`);
      const data = await response.json();
      setOppgaver(data.oppgaver || []);
    } catch (error) {
      console.error('Feil ved lasting av oppgaver:', error);
    } finally {
      setLoading(false);
    }
  };

  const opprettOppgave = async (e) => {
    e.preventDefault();

    if (!nyOppgave.tittel) {
      alert('Tittel er påkrevd');
      return;
    }

    try {
      const response = await api('/oppgaver', {
        method: 'POST',
        body: JSON.stringify({
          sak_id: parseInt(sakId),
          ...nyOppgave,
          estimert_timer: parseFloat(nyOppgave.estimert_timer) || 1
        })
      });

      if (response.ok) {
        setShowNyOppgave(false);
        setNyOppgave({
          tittel: '',
          beskrivelse: '',
          type: 'dokument',
          prioritet: 'middels',
          estimert_timer: 1,
          forfallsdato: ''
        });
        setAiEstimat(null);
        lastOppgaver();
      } else {
        const error = await response.json();
        alert(`Feil: ${error.error || 'Kunne ikke opprette oppgave'}`);
      }
    } catch (error) {
      console.error('Feil ved oppretting av oppgave:', error);
      alert('Kunne ikke opprette oppgave');
    }
  };

  const oppdaterOppgave = async (e) => {
    e.preventDefault();

    if (!redigerOppgave.tittel) {
      alert('Tittel er påkrevd');
      return;
    }

    try {
      const response = await api(`/oppgaver/${redigerOppgave.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          tittel: redigerOppgave.tittel,
          beskrivelse: redigerOppgave.beskrivelse,
          type: redigerOppgave.type,
          prioritet: redigerOppgave.prioritet,
          estimert_timer: parseFloat(redigerOppgave.estimert_timer) || 1,
          forfallsdato: redigerOppgave.forfallsdato
        })
      });

      if (response.ok) {
        setRedigerOppgave(null);
        setAiEstimat(null);
        lastOppgaver();
      } else {
        const error = await response.json();
        alert(`Feil: ${error.error || 'Kunne ikke oppdatere oppgave'}`);
      }
    } catch (error) {
      console.error('Feil ved oppdatering av oppgave:', error);
      alert('Kunne ikke oppdatere oppgave');
    }
  };

  const oppdaterStatus = async (oppgaveId, nyStatus) => {
    try {
      const response = await api(`/oppgaver/${oppgaveId}`, {
        method: 'PUT',
        body: JSON.stringify({
          status: nyStatus,
          ...(nyStatus === 'fullført' ? { fullført_dato: new Date().toISOString().split('T')[0] } : {})
        })
      });

      if (response.ok) {
        lastOppgaver();
      }
    } catch (error) {
      console.error('Feil ved oppdatering av oppgave:', error);
    }
  };

  const slettOppgave = async (oppgaveId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne oppgaven?')) {
      return;
    }

    try {
      const response = await api(`/oppgaver/${oppgaveId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        lastOppgaver();
      }
    } catch (error) {
      console.error('Feil ved sletting av oppgave:', error);
    }
  };

  const hentAiEstimat = async () => {
    if (!nyOppgave.tittel) {
      alert('Legg inn en tittel først for å få AI-estimat');
      return;
    }

    try {
      setLasterEstimat(true);
      setAiEstimat(null);

      const response = await api('/oppgaver/estimate', {
        method: 'POST',
        body: JSON.stringify({
          tittel: nyOppgave.tittel,
          beskrivelse: nyOppgave.beskrivelse,
          type: nyOppgave.type,
          prioritet: nyOppgave.prioritet,
          sak_id: parseInt(sakId)
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAiEstimat(data);

        // Oppdater estimert_timer automatisk
        setNyOppgave({
          ...nyOppgave,
          estimert_timer: data.estimert_timer
        });
      } else {
        const error = await response.json();
        alert(`Kunne ikke hente AI-estimat: ${error.error || 'Ukjent feil'}`);
      }
    } catch (error) {
      console.error('Feil ved henting av AI-estimat:', error);
      alert('Kunne ikke hente AI-estimat. Se konsoll for detaljer.');
    } finally {
      setLasterEstimat(false);
    }
  };

  const getPrioritetFarge = (prioritet) => {
    switch (prioritet) {
      case 'kritisk': return '#d32f2f';
      case 'høy': return '#f57c00';
      case 'middels': return '#fbc02d';
      case 'lav': return '#388e3c';
      default: return '#757575';
    }
  };

  const hentTypeEmoji = (type) => {
    switch (type) {
      case 'dokument': return '📄';
      case 'juridisk_vurdering': return '⚖️';
      case 'mail': return '✉️';
      case 'møte': return '🤝';
      case 'prosess': return '📋';
      case 'admin': return '⚙️';
      default: return '📌';
    }
  };

  if (loading) {
    return <div className="oppgave-loading">Laster oppgaver...</div>;
  }

  return (
    <div className="oppgave-liste-container">
      <div className="oppgave-header">
        <h3>📋 Gjøremål ({oppgaver.length})</h3>
        <button
          className="btn-primary"
          onClick={() => setShowNyOppgave(true)}
        >
          ➕ Ny oppgave
        </button>
      </div>

      {/* Statistikk */}
      <div className="oppgave-stats">
        <div className="stat-item">
          <span className="stat-label">Planlagt:</span>
          <span className="stat-value">{oppgaver.filter(o => o.status === 'planlagt').length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Pågående:</span>
          <span className="stat-value">{oppgaver.filter(o => o.status === 'pågående').length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Fullført:</span>
          <span className="stat-value">{oppgaver.filter(o => o.status === 'fullført').length}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Estimert tid:</span>
          <span className="stat-value">{oppgaver.reduce((sum, o) => sum + (o.estimert_timer || 0), 0).toFixed(1)}t</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Faktisk tid:</span>
          <span className="stat-value">
            {oppgaver.reduce((sum, o) => sum + (o.faktisk_timer || 0), 0).toFixed(1)}t
          </span>
        </div>
        {(() => {
          const totalEstimert = oppgaver.reduce((sum, o) => sum + (o.estimert_timer || 0), 0);
          const totalFaktisk = oppgaver.reduce((sum, o) => sum + (o.faktisk_timer || 0), 0);
          const prosent = totalEstimert > 0 ? Math.round((totalFaktisk / totalEstimert) * 100) : 0;
          return totalEstimert > 0 && (
            <div className="stat-item">
              <span className="stat-label">Forbruk:</span>
              <span className="stat-value" style={{
                color: prosent >= 100 ? '#dc3545' : prosent >= 80 ? '#ffc107' : '#28a745',
                fontWeight: '700'
              }}>
                {prosent}%
              </span>
            </div>
          );
        })()}
      </div>

      {/* Oppgaveliste */}
      {oppgaver.length === 0 ? (
        <div className="empty-state">
          <p>Ingen oppgaver ennå</p>
          <p className="hint">Klikk "Ny oppgave" for å legge til et gjøremål</p>
        </div>
      ) : (
        <div className="oppgaver-grid">
          {oppgaver.map((oppgave) => (
            <div
              key={oppgave.id}
              className={`oppgave-card status-${oppgave.status}`}
              style={{ borderLeft: `4px solid ${getPrioritetFarge(oppgave.prioritet)}`, cursor: 'pointer' }}
              onClick={() => setRedigerOppgave(oppgave)}
            >
              <div className="oppgave-header-row" onClick={(e) => e.stopPropagation()}>
                <div className="oppgave-type">
                  {hentTypeEmoji(oppgave.type)} {oppgave.type}
                </div>
                <div className="oppgave-actions">
                  {oppgave.status !== 'fullført' && (
                    <>
                      <select
                        value={oppgave.status}
                        onChange={(e) => oppdaterStatus(oppgave.id, e.target.value)}
                        className="status-select"
                      >
                        <option value="planlagt">Planlagt</option>
                        <option value="pågående">Pågående</option>
                        <option value="fullført">Fullført</option>
                        <option value="utsatt">Utsatt</option>
                      </select>
                    </>
                  )}
                  <button
                    onClick={() => slettOppgave(oppgave.id)}
                    className="btn-delete-small"
                    title="Slett oppgave"
                  >
                    🗑️
                  </button>
                </div>
              </div>

              <h4>{oppgave.tittel}</h4>

              {oppgave.beskrivelse && (
                <p className="oppgave-beskrivelse">{oppgave.beskrivelse}</p>
              )}

              <div className="oppgave-meta">
                <div className="meta-item">
                  <span className="meta-label">Prioritet:</span>
                  <span
                    className="prioritet-badge"
                    style={{ backgroundColor: getPrioritetFarge(oppgave.prioritet) }}
                  >
                    {oppgave.prioritet}
                  </span>
                </div>

                <div className="meta-item">
                  <span className="meta-label">Estimat:</span>
                  <span className="meta-value">{oppgave.estimert_timer || 0}t</span>
                </div>

                {(oppgave.faktisk_timer !== null && oppgave.faktisk_timer !== undefined) && (
                  <div className="meta-item">
                    <span className="meta-label">Brukt:</span>
                    <span className="meta-value" style={{
                      color: oppgave.faktisk_timer > oppgave.estimert_timer ? '#dc3545' : '#28a745',
                      fontWeight: '600'
                    }}>
                      {oppgave.faktisk_timer}t
                      {oppgave.estimert_timer > 0 && (
                        <span style={{ fontSize: '0.85em', marginLeft: '4px', opacity: 0.8 }}>
                          ({Math.round((oppgave.faktisk_timer / oppgave.estimert_timer) * 100)}%)
                        </span>
                      )}
                    </span>
                  </div>
                )}

                {oppgave.forfallsdato && (
                  <div className="meta-item">
                    <span className="meta-label">Frist:</span>
                    <span className="meta-value">
                      {new Date(oppgave.forfallsdato).toLocaleDateString('nb-NO')}
                    </span>
                  </div>
                )}

                {oppgave.ansvarlig_advokat && (
                  <div className="meta-item">
                    <span className="meta-label">Ansvarlig:</span>
                    <span className="meta-value">{oppgave.advokat_navn || oppgave.ansvarlig_advokat}</span>
                  </div>
                )}
              </div>

              {oppgave.status === 'fullført' && oppgave.fullført_dato && (
                <div className="oppgave-fullført">
                  ✓ Fullført {new Date(oppgave.fullført_dato).toLocaleDateString('nb-NO')}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal: Ny oppgave */}
      {showNyOppgave && (
        <div className="modal-overlay" onClick={() => { setShowNyOppgave(false); setAiEstimat(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Ny oppgave</h2>

            <form onSubmit={opprettOppgave}>
              <div className="form-group">
                <label>Tittel *</label>
                <input
                  type="text"
                  value={nyOppgave.tittel}
                  onChange={(e) => setNyOppgave({...nyOppgave, tittel: e.target.value})}
                  placeholder="F.eks. Gjennomgå tilstandsrapport"
                  required
                />
              </div>

              <div className="form-group">
                <label>Beskrivelse</label>
                <textarea
                  value={nyOppgave.beskrivelse}
                  onChange={(e) => setNyOppgave({...nyOppgave, beskrivelse: e.target.value})}
                  placeholder="Detaljer om oppgaven..."
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={nyOppgave.type}
                    onChange={(e) => setNyOppgave({...nyOppgave, type: e.target.value})}
                  >
                    <option value="dokument">📄 Dokument</option>
                    <option value="juridisk_vurdering">⚖️ Juridisk vurdering</option>
                    <option value="mail">✉️ E-post/Korrespondanse</option>
                    <option value="møte">🤝 Møte</option>
                    <option value="prosess">📋 Prosesshandling</option>
                    <option value="admin">⚙️ Administrativt</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Prioritet</label>
                  <select
                    value={nyOppgave.prioritet}
                    onChange={(e) => setNyOppgave({...nyOppgave, prioritet: e.target.value})}
                  >
                    <option value="lav">Lav</option>
                    <option value="middels">Middels</option>
                    <option value="høy">Høy</option>
                    <option value="kritisk">Kritisk</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Estimert tid (timer)</label>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <input
                      type="number"
                      step="0.5"
                      min="0.5"
                      value={nyOppgave.estimert_timer}
                      onChange={(e) => setNyOppgave({...nyOppgave, estimert_timer: e.target.value})}
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={hentAiEstimat}
                      disabled={lasterEstimat || !nyOppgave.tittel}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {lasterEstimat ? '⏳ Beregner...' : '🤖 AI-estimat'}
                    </button>
                  </div>

                  {aiEstimat && (
                    <div style={{
                      marginTop: '10px',
                      padding: '12px',
                      background: aiEstimat.ai_generert ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#f5f5f5',
                      color: aiEstimat.ai_generert ? 'white' : '#333',
                      borderRadius: '8px',
                      fontSize: '0.9rem'
                    }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                        💡 Anbefalt: {aiEstimat.estimert_timer}t
                        {aiEstimat.konfidensintervall && (
                          <span style={{ fontWeight: 'normal', opacity: 0.9 }}>
                            {' '}({aiEstimat.konfidensintervall[0]}-{aiEstimat.konfidensintervall[1]}t)
                          </span>
                        )}
                      </div>

                      <div style={{ fontSize: '0.85rem', opacity: 0.95, marginBottom: '8px' }}>
                        {aiEstimat.begrunnelse}
                      </div>

                      {aiEstimat.historisk_data && aiEstimat.historisk_data.antall > 0 && (
                        <div style={{ fontSize: '0.8rem', opacity: 0.85, borderTop: '1px solid rgba(255,255,255,0.3)', paddingTop: '8px', marginTop: '8px' }}>
                          <strong>Historikk:</strong> {aiEstimat.historisk_data.antall} lignende oppgaver
                          {' • '}Snitt: {aiEstimat.historisk_data.gjennomsnitt}t
                          {' • '}Spredning: {aiEstimat.historisk_data.min}-{aiEstimat.historisk_data.max}t
                        </div>
                      )}

                      {aiEstimat.fallback && (
                        <div style={{ fontSize: '0.75rem', opacity: 0.75, marginTop: '5px', fontStyle: 'italic' }}>
                          ⚠️ Fallback-estimat (AI utilgjengelig)
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Forfallsdato</label>
                  <input
                    type="date"
                    value={nyOppgave.forfallsdato}
                    onChange={(e) => setNyOppgave({...nyOppgave, forfallsdato: e.target.value})}
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowNyOppgave(false); setAiEstimat(null); }}>
                  Avbryt
                </button>
                <button type="submit" className="btn-primary">
                  Opprett oppgave
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Rediger oppgave */}
      {redigerOppgave && (
        <div className="modal-overlay" onClick={() => { setRedigerOppgave(null); setAiEstimat(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>✏️ Rediger oppgave</h2>

            <form onSubmit={oppdaterOppgave}>
              <div className="form-group">
                <label>Tittel *</label>
                <input
                  type="text"
                  value={redigerOppgave.tittel}
                  onChange={(e) => setRedigerOppgave({...redigerOppgave, tittel: e.target.value})}
                  placeholder="F.eks. Gjennomgå tilstandsrapport"
                  required
                />
              </div>

              <div className="form-group">
                <label>Beskrivelse</label>
                <textarea
                  value={redigerOppgave.beskrivelse || ''}
                  onChange={(e) => setRedigerOppgave({...redigerOppgave, beskrivelse: e.target.value})}
                  placeholder="Detaljer om oppgaven..."
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={redigerOppgave.type}
                    onChange={(e) => setRedigerOppgave({...redigerOppgave, type: e.target.value})}
                  >
                    <option value="dokument">📄 Dokument</option>
                    <option value="juridisk_vurdering">⚖️ Juridisk vurdering</option>
                    <option value="mail">✉️ E-post/Korrespondanse</option>
                    <option value="møte">🤝 Møte</option>
                    <option value="prosess">📋 Prosesshandling</option>
                    <option value="admin">⚙️ Administrativt</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Prioritet</label>
                  <select
                    value={redigerOppgave.prioritet}
                    onChange={(e) => setRedigerOppgave({...redigerOppgave, prioritet: e.target.value})}
                  >
                    <option value="lav">Lav</option>
                    <option value="middels">Middels</option>
                    <option value="høy">Høy</option>
                    <option value="kritisk">Kritisk</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Estimert tid (timer)</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0.5"
                    value={redigerOppgave.estimert_timer}
                    onChange={(e) => setRedigerOppgave({...redigerOppgave, estimert_timer: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label>Forfallsdato</label>
                  <input
                    type="date"
                    value={redigerOppgave.forfallsdato || ''}
                    onChange={(e) => setRedigerOppgave({...redigerOppgave, forfallsdato: e.target.value})}
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setRedigerOppgave(null); setAiEstimat(null); }}>
                  Avbryt
                </button>
                <button type="submit" className="btn-primary">
                  Lagre endringer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
